/**
 * CANONICAL TESLA FACTS — single source of truth.
 * ----------------------------------------------------------------------------
 * Every page, FAQ, JSON-LD blob, and install step that states a Tesla fact must
 * align to the values here. Do NOT hardcode a competing value elsewhere; import
 * from this file (or, for prose, match these values exactly). The anti-slop CI
 * check (scripts/qa/anti-slop-check.mjs) blocks known-bad variants like the
 * legacy `Boombox/Locked/` USB path.
 *
 * Why this exists: a May 2026 AI-slop audit found the codebase asserted
 * conflicting facts across faq-data.ts, schema.ts, model-data.ts, and the
 * install flows (wrong USB path, brittle firmware versions, contradictory model
 * support). Conflicting "facts" read as hallucinated slop and erode trust.
 *
 * Sources & confidence live in docs/CANONICAL_FACTS.md. Items marked
 * NEEDS-CONFIRMATION there must not be asserted as precise fact in UI copy.
 */

import { PUBLIC_SOUND_COUNT_LABEL } from './public-sound-count'

export const TESLA_FACTS = {
  /**
   * Where the lock-sound file goes on the USB drive.
   * The site standardizes on `Boombox/LockChime.wav` (its owner-tested
   * convention). There is definitively NO `Locked/` subfolder — that was a bug.
   * NOTE: some external Tesla guides say the file belongs at the USB ROOT, not
   * inside `Boombox/`. That conflict is unresolved — see docs/CANONICAL_FACTS.md
   * (item 1, NEEDS-CONFIRMATION). Do not flip this value without confirming on
   * a real car; a wrong path breaks every install.
   */
  usb: {
    /** Site-canonical path. There is NO `Locked/` subfolder. */
    path: 'Boombox/LockChime.wav',
    folder: 'Boombox',
    filename: 'LockChime.wav',
    /** Both work; FAT32 is the most universally compatible. */
    formats: ['FAT32', 'exFAT'] as const,
    /** Must be a front DATA port. Rear ports are power-only. Exact port varies by model/year. */
    portNote: 'a front USB data port (the rear ports are power-only)',
  },

  /**
   * Audio spec. The ONLY hard Tesla requirement is: a WAV file named exactly
   * `LockChime.wav`. The numeric specs below are widely-used best practice for
   * clean playback — they are NOT published Tesla requirements, so describe them
   * as "recommended", never "Tesla requires".
   */
  audio: {
    requiredFormat: 'WAV',
    requiredFilename: 'LockChime.wav',
    /** Recommended (best-practice), not an official Tesla spec. */
    recommended: {
      sampleRate: '44.1 kHz',
      bitDepth: '16-bit',
      duration: '1–5 seconds',
      /**
       * Machine-readable ceiling for the duration guidance above, in seconds.
       * Exists because three places hardcoded their own number and disagreed:
       * the trimmer, the voice generator and lib/audio-utils all used 5 while
       * lib/sound-utils used 2 and told the user "Tesla allows up to 2s".
       * Both figures were also framed as a Tesla limit, which this file has
       * always denied: the duration line is RECOMMENDED, not a published
       * Tesla spec. Read this value, do not restate a number.
       */
      maxDurationSeconds: 5,
      maxFileSize: 'under 1 MB',
    },
    /**
     * Do NOT emit a precise bitrate as machine-readable fact for arbitrary
     * user-uploaded WAVs — it varies per file. Left undefined on purpose.
     */
    bitrate: undefined as string | undefined,
  },

  /**
   * Firmware. The custom LOCK SOUND feature shipped in Tesla's 2023 Holiday
   * Update (software 2023.44.x), December 2023 — distinct from Boombox itself,
   * which arrived in the 2020 Holiday Update. Verified against release notes
   * (see docs/CANONICAL_FACTS.md). Prefer the non-brittle `label` in UI copy.
   */
  firmware: {
    featureSince: 'Tesla’s 2023 Holiday Update (software 2023.44)',
    /** Safe, accurate, model-agnostic sentence for FAQ/UI copy. */
    label:
      'Custom lock sounds have been available since Tesla’s 2023 Holiday Update (software 2023.44), so virtually every Tesla on current software supports them. If you don’t see the Lock Sound option, update your software from Controls → Software.',
  },

  /**
   * Model support. The real requirement is the external pedestrian-warning
   * speaker (PWS / AVAS). Year cutoffs below track when each line shipped it.
   * MEDIUM confidence on exact cutoffs (individual VINs vary).
   */
  support: {
    requirement: 'an external pedestrian-warning speaker',
    requirementShort: 'external speaker',
    models: {
      'model-3': { label: 'Model 3', supported: 'Built September 2019 or later' },
      'model-y': { label: 'Model Y', supported: 'All model years' },
      'model-s': { label: 'Model S', supported: 'The 2021 refresh or later' },
      'model-x': { label: 'Model X', supported: 'The 2021 refresh or later' },
      cybertruck: { label: 'Cybertruck', supported: 'All' },
    },
  },

  /**
   * Install time. ONE number site-wide: the guide itself claims two minutes
   * ("USB stick. Two minutes."), so every other surface (nav pill, library
   * install-path panel, HowTo JSON-LD totalTime) must say the same. Never
   * hardcode "5 minutes" or "PT5M" for the install flow again.
   */
  install: {
    minutes: 2,
    /** Prose form: "2 minutes". */
    label: '2 minutes',
    /** ISO 8601 duration for schema.org HowTo.totalTime. */
    iso: 'PT2M',
  },

  /**
   * Library size. The single source of truth is PUBLIC_SOUND_COUNT_LABEL
   * (lib/public-sound-count.ts) — re-exported here so fact consumers have one
   * import. NEVER hardcode a count like "900+"/"950+"/"1,000+"; those are
   * banned stale strings (see the deploy-smoke checklist + stale-copy test).
   */
  soundCountFallback: PUBLIC_SOUND_COUNT_LABEL,

  /**
   * Paint Shop wrap PNGs. Sibling folder on the same USB as LockChime.wav.
   * Source: Tesla custom-wraps README (docs/CANONICAL_FACTS.md item 8).
   * tesla-wrap.design is an independent free browser studio, not a Tesla product.
   */
  wrap: {
    folder: 'Wraps',
    path: 'Wraps/',
    format: 'PNG',
    menu: 'Toybox → Paint Shop → Wraps',
    designer: {
      name: 'tesla-wrap.design',
      url: 'https://tesla-wrap.design',
      role: 'Paint Shop wrap designer',
    },
  },

  /** Canonical Tesla-facing source links. See docs/CANONICAL_FACTS.md for confidence notes. */
  sources: {
    ownersManual: 'https://www.tesla.com/ownersmanual',
    support: 'https://www.tesla.com/support',
    customWraps: 'https://github.com/teslamotors/custom-wraps',
  },
} as const

export type TeslaModelKey = keyof typeof TESLA_FACTS.support.models
